import { useMemo, useState } from 'react'

import {
  useLazyCategoriesQuery,
  useLazyGetCategoryNamesFromIdsQuery,
} from '../../../../store/api'
import type {
  MetaResponse,
  PaginatedQueryParams,
} from '../../../../store/types'
import type { Category } from '../../../../store/types/category'
import type { DropdownOption } from '../../types'
import FilterAsyncPaginateDropdown from '../filter-async-paginate'

type CategoriesDropdownProps<B extends boolean> = {
  name?: string
  onChange?: B extends true
    ? (item: DropdownOption[]) => void
    : (item: DropdownOption) => void
  onSubmit?: () => void
  isMulti?: B
  isClearable?: boolean
  isCreatable?: boolean
  exclude?: number
  value?: DropdownOption
  styles?: any
  components?: any
  filterValues?: any
  extendedStyles?: boolean
}

const categoriesToDropdownOptions = (category: Category): DropdownOption => ({
  label: category.name,
  value: category.id.toString(),
})

export default function CategoriesFilterDropdown({
  onChange,
  isClearable = false,
  exclude = undefined,
  onSubmit,
  filterValues,
}: CategoriesDropdownProps<boolean>) {
  const [fetcher] = useLazyCategoriesQuery()
  const categoriesFetcher = async (params: PaginatedQueryParams) => {
    const res = await fetcher({
      ...params,
      orderBy: 'name',
      orderDirection: 'asc',
    })
    const response: { data?: DropdownOption[]; meta?: MetaResponse<Category> } =
      {}
    if (exclude !== undefined) {
      const filteredResponse: any = res.data?.data
        .filter((category: any) => category?.id !== exclude)
        .map(categoriesToDropdownOptions)
      response.data = filteredResponse
    } else {
      response.data = res.data?.data.map(categoriesToDropdownOptions)
    }

    response.meta = res.data?.meta

    return response
  }

  const [defaultCategoryValues, setDefaultCategoryValues] = useState<
    Array<{ label: string; value: string }>
  >([])

  const [getCategoryNames, { isLoading }] =
    useLazyGetCategoryNamesFromIdsQuery()

  useMemo(() => {
    if (filterValues) {
      const values = filterValues
        .split(',')
        .map((n: string) => Math.abs(Number(n)))
      if (
        values.length > 0 &&
        !values.every(
          (i: number) =>
            defaultCategoryValues.findIndex(
              (item) => item.value === i.toString(),
            ) > -1,
        )
      ) {
        getCategoryNames({ ids: filterValues })
          .then((response) => {
            const items = response?.data.map((item: any) => ({
              label: item?.name,
              value: item?.id.toString(),
              isNegative: item?.is_negative,
            }))
            const idList = filterValues
              .split(',')
              .map((item: string) => Math.abs(Number(item)).toFixed())
            items.sort(
              (i1: any, i2: any) =>
                idList.indexOf(i1.value) - idList.indexOf(i2.value),
            )

            setDefaultCategoryValues(items)
          })
          .catch((err) => {})
      }
    } else {
      setDefaultCategoryValues([])
    }
  }, [filterValues])

  return (
    <FilterAsyncPaginateDropdown
      onSubmit={onSubmit}
      isReady={!isLoading}
      fetcher={categoriesFetcher}
      defaultValue={defaultCategoryValues}
      onChange={onChange}
      isClearable={isClearable}
    />
  )
}
